import { differenceInHours, endOfWeek, parseISO, startOfWeek } from "date-fns";
import {
  SelectedShippingGroup_T,
  ShippingDetailRecord_T,
} from "../types/Shipping";

export interface ValidationError_T {
  fulfillment_group_id: number;
  name: string;
  error?: string;
}

export interface ShipmentValidationError_T {
  shipment_id: number;
  delivery_name: string;
  error?: string;
}

export interface MissingDestinationItem_T {
  fulfillmentGroupId: number;
  groupName: string;
  deliveryId: number;
  deliveryName: string;
}

export const getPendingVolumeUpgradeShipments = (
  shipments: ShippingDetailRecord_T[]
): ShippingDetailRecord_T[] => {
  return shipments.filter(
    (shipment) =>
      shipment.volume_upgrade_scheduled && !shipment.volume_upgrade_merged
  );
};

// apple customer validation
export const hasInvalidAppleCustomerSelection = (
  customerId: number,
  partiallySelectedGroups: SelectedShippingGroup_T[]
): boolean => {
  if (customerId === 1) {
    return partiallySelectedGroups.length > 0;
  }
  return false;
};

export const hasExportDateOutsideShippingWeek = (
  targetDate: string,
  selectedGroups: SelectedShippingGroup_T[]
): boolean => {
  if (!targetDate) return false;

  try {
    const newTargetDate = new Date(targetDate);
    const weekStart = startOfWeek(newTargetDate, { weekStartsOn: 1 }); // monday
    const weekEnd = endOfWeek(newTargetDate, { weekStartsOn: 1 }); // sunday

    for (const group of selectedGroups) {
      for (const shipment of group.shipments) {
        if (shipment.export_completed_at) {
          const exportDate = new Date(shipment.export_completed_at);
          if (exportDate < weekStart || exportDate > weekEnd) {
            return true;
          }
        }
      }
    }
  } catch (error) {
    console.error("Date processing error:", error);
    return false;
  }
  return false;
};

export const getMissingDestinations = (
  selectedGroups: SelectedShippingGroup_T[]
): MissingDestinationItem_T[] => {
  const missingDestinations: MissingDestinationItem_T[] = [];

  selectedGroups.forEach((group) => {
    group.shipments.forEach((shipment) => {
      if (!shipment.dest_uri_address_template) {
        missingDestinations.push({
          fulfillmentGroupId: group.fulfillmentGroupId,
          groupName: group.name,
          deliveryId: shipment.delivery_id || 0,
          deliveryName: shipment.delivery_name || "Unknown Delivery",
        });
      }
    });
  });

  return missingDestinations;
};

export const hasValidDeliveryStatus = (
  shipment: ShippingDetailRecord_T
): boolean => {
  const validStatuses = ["Waiting to ship", "Failed at shipping"];
  return validStatuses.includes(shipment.fulfillment_status || "");
};

export const hasValidExportStatus = (
  shipment: ShippingDetailRecord_T
): boolean => {
  const validStatuses = [
    "Waiting to export",
    "Failed at export",
    "Canceled at export",
    "Export complete",
    "Waiting to ship",
    "Successfully shipped",
    "Failed at shipping",
    "Checksum required",
    "Failed at checksum",
  ];
  return validStatuses.includes(shipment.fulfillment_status || "");
};

export const hasValidDeliveryStatusForGroup = (
  selectedGroups: SelectedShippingGroup_T[]
): boolean => {
  for (const group of selectedGroups) {
    for (const shipment of group.shipments) {
      if (!hasValidDeliveryStatus(shipment)) {
        return false;
      }
    }
  }
  return true;
};

export const hasValidExportStatusForGroup = (
  selectedGroups: SelectedShippingGroup_T[]
): boolean => {
  for (const group of selectedGroups) {
    for (const shipment of group.shipments) {
      if (!hasValidExportStatus(shipment)) {
        return false;
      }
    }
  }
  return true;
};

export const isReadyForChecksum = (
  shipment: ShippingDetailRecord_T
): boolean => {
  return (
    shipment.fulfillment_status === "Checksum required" ||
    shipment.fulfillment_status === "Failed at checksum"
  );
};

export const isWithinDeliveryTimeframe = (
  shipments: ShippingDetailRecord_T[]
): boolean => {
  const successfulDeliveries = shipments.filter((s) => isReadyForChecksum(s));

  if (successfulDeliveries.length === 0) return false;

  // get first successful delivery time
  const firstDeliveryTime = Math.min(
    ...successfulDeliveries.map((s) =>
      s.fulfillment_completed_at
        ? parseISO(s.fulfillment_completed_at).getTime()
        : Infinity
    )
  );

  // current time is within 12 hours of first successful delivery
  const firstDeliveryDate = new Date(firstDeliveryTime);
  const currentTime = new Date();

  const hoursSinceFirstDelivery = Math.abs(
    differenceInHours(currentTime, firstDeliveryDate)
  );

  return hoursSinceFirstDelivery <= 12;
};
